import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ErrorBoundary } from '../ErrorBoundary';
import { useToast } from '../../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { useDarkMode } from '../../context/DarkModeContext';
import RetroForm from './RetroForm';

interface RetroCreatorProps {
  onClose: () => void;
}

export const RetroCreator: React.FC<RetroCreatorProps> = ({ onClose }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);

  // Add keyboard event listener for Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, isSubmitting]);

  const handleSave = async (formData: {
    title: string;
    description: string;
    startDate: Date;
    endDate: Date;
    frameworkType: string;
    sections: Array<{ title: string; description: string; content: string }>;
  }) => {
    try {
      setIsSubmitting(true);
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Failed to get current user');
      }

      // Create the retrospective
      const { data: retro, error: retroError } = await supabase
        .from('retrospectives')
        .insert({
          title: formData.title,
          description: formData.description,
          framework_type: formData.frameworkType,
          start_date: formData.startDate.toISOString(),
          end_date: formData.endDate.toISOString(),
          user_id: user.id
        })
        .select()
        .single();

      if (retroError) throw retroError;

      // Create the initial entries for each section
      if (retro) {
        const entries = formData.sections.map(section => ({
          retrospective_id: retro.id,
          content: section.content,
          category: section.title.toLowerCase().replace(/\s+/g, '-'),
          created_at: new Date().toISOString()
        }));

        const { error: entriesError } = await supabase
          .from('retrospective_entries')
          .insert(entries);

        if (entriesError) throw entriesError;
      }

      showToast('Retrospective created successfully', 'success');
      onClose();
      // Navigate to the RetroViewer in edit mode
      navigate(`/retrospectives/${retro.id}?edit=true`);
    } catch (error) {
      console.error('Failed to create retrospective:', error);
      showToast('Failed to create retrospective. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitClick = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return createPortal(
    <ErrorBoundary>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-100 overflow-y-auto">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl w-full max-w-2xl my-4`}>
          {/* Header */}
          <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center sticky top-0 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-t-lg z-10`}>
            <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              New Retro
            </h1>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSubmitClick}
                disabled={isSubmitting}
                className={`px-4 py-2 rounded-md text-white bg-blue-500 hover:bg-blue-600 transition-colors duration-200 flex items-center gap-2 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </button>
              <button
                onClick={onClose}
                className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="p-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
            <RetroForm ref={formRef} onSave={handleSave} />
          </div>
        </div>
      </div>
    </ErrorBoundary>,
    document.body
  );
};

export default RetroCreator; 